// Short edit interface for GameBlock: referees only

import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import MultiSelect from './MultiSelect';
import Spinner from './Spinner';
import RadioButton from './RadioButton';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { tournamentGameRefereesSet } from '../utils/firebase';
import { useRefereeOptions } from '../utils/hooks';
import Fieldset from './Fieldset';
import { intervalsOverlap } from './Field';
import { getTeamDivision } from '../utils/misc';
import './GameBlockRefs.scss';

const ApplyTo = {
  THIS_GAME: 'this-game',
  DIVISION_ON_FIELD: 'division-on-field',
  ALL_ON_FIELD: 'all-on-field',
};

// Checks referee overlaps across OTHER fields only (same date, time overlap)
function getOverlappedRefIds({ targets, games, refIds }) {
  const overlapped = new Set();
  const refSet = new Set(refIds);

  for (const tg of targets) {
    for (const g of games) {
      if (g.id === tg.id) continue;
      if (g.date !== tg.date) continue;
      if (g.field === tg.field) continue;
      if (!g.referees?.length) continue;
      if (!intervalsOverlap(tg.start, tg.duration, g.start, g.duration))
        continue;

      for (const rId of g.referees) if (refSet.has(rId)) overlapped.add(rId);
    }
  }

  return overlapped;
}

export default function GameBlockRefs({ id, game }) {
  const { closeModal, isModalOpen } = useModal();
  const [spinner, setSpinner] = useState(false);
  const { tournamentsById, refereesById, teamsById } = useFirebase();
  // The body outlives the closed modal, so its reads stand down with it
  const options = useRefereeOptions(isModalOpen ? id : null, game?.referees);

  const tournament = tournamentsById.get(id);
  const games = tournament?.games || [];

  const getGameDivision = (g) => {
    if (g?.division) return g.division;

    if (g?.team1) {
      return getTeamDivision(teamsById.get(g.team1));
    }

    if (g?.team2) {
      return getTeamDivision(teamsById.get(g.team2));
    }

    return '';
  };

  const initialValues = useMemo(
    () => ({
      referees: game?.referees ?? [],
      applyTo: ApplyTo.THIS_GAME,
    }),
    [game],
  );

  const validationSchema = new Yup.ObjectSchema({
    referees: Yup.array(),
    applyTo: Yup.string().oneOf(Object.values(ApplyTo)).required('Required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSpinner(true);

      try {
        const refIds = (values.referees || []).filter(Boolean);

        let targets = [game];

        // The schedule shows one day, so a bulk target stays inside it
        const onFieldThatDay = (g) =>
          g.field === game.field && g.date === game.date;

        if (values.applyTo === ApplyTo.ALL_ON_FIELD) {
          targets = games.filter(onFieldThatDay);
        } else if (values.applyTo === ApplyTo.DIVISION_ON_FIELD) {
          const div = getGameDivision(game);
          targets = games.filter(
            (g) => onFieldThatDay(g) && getGameDivision(g) === div,
          );
        }

        // Referee overlap check (same day, time overlap, other fields)
        const overlappedRefIds = getOverlappedRefIds({
          targets,
          games,
          refIds,
        });

        if (overlappedRefIds.size) {
          const names = [...overlappedRefIds]
            .map((rid) => refereesById.get(rid)?.name)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));

          toast.error(
            `Referee assignment can't be applied: time overlap for ${names.join(
              ', ',
            )}`,
          );

          return;
        }

        await Promise.all(
          targets.map((g) =>
            tournamentGameRefereesSet({
              id,
              gameId: g.id,
              referees: refIds,
            }),
          ),
        );

        closeModal();
      } catch (e) {
        toast.error(e?.message || 'Unknown error');
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <form className='game-block-refs' onSubmit={formik.handleSubmit}>
      <div className='field-ref-wrap'>
        <MultiSelect
          options={options}
          value={formik.values.referees}
          onChange={(v) => formik.setFieldValue('referees', v)}
          placeholder='Referees'
          error={formik.touched.referees && formik.errors.referees}
          search
        />
      </div>

      <Fieldset label='Apply to' className='apply-to'>
        <RadioButton
          label='This game'
          name='applyTo'
          value={ApplyTo.THIS_GAME}
          selectedValue={formik.values.applyTo}
          onChange={() => formik.setFieldValue('applyTo', ApplyTo.THIS_GAME)}
        />
        <RadioButton
          label='Division games on this field this day'
          name='applyTo'
          value={ApplyTo.DIVISION_ON_FIELD}
          selectedValue={formik.values.applyTo}
          onChange={() =>
            formik.setFieldValue('applyTo', ApplyTo.DIVISION_ON_FIELD)
          }
        />
        <RadioButton
          label='All games on this field this day'
          name='applyTo'
          value={ApplyTo.ALL_ON_FIELD}
          selectedValue={formik.values.applyTo}
          onChange={() => formik.setFieldValue('applyTo', ApplyTo.ALL_ON_FIELD)}
        />
      </Fieldset>

      <div className='buttons'>
        <button type='button' className='bw' onClick={closeModal}>
          Cancel
        </button>
        <button type='submit' disabled={spinner}>
          Save
        </button>
      </div>

      <Spinner show={spinner} />
    </form>
  );
}
